import classNames from 'classnames';
import React from 'react';
import { CONTEXT_BASE, CONTEXT_BONUSES, displayPct, EffectiveScores } from './utils/curationScoring';
import { useCurationReview } from './state/CurationReviewContext';
import OverrideBox from './OverrideBox';

const styles = require('./ExperienceBreakdown.module.scss');

const FACTOR_LABELS: Record<string, string> = {
  company_type: 'Company type',
  industry: 'Industry',
  role: 'Role',
  tech_stack: 'Tech stack',
};

export interface Props {
  scores: EffectiveScores;
}

export default function ExperienceBreakdown({ scores }: Props) {
  const { experienceMax, experienceMin, state } = useCurationReview();

  const experience = state.payload ? state.payload.experience : null;
  const relevantYears = experience ? experience.relevant_years : null;
  const factors = experience && experience.context ? experience.context.factors : [];
  const reasoning = experience && experience.context ? experience.context.reasoning : '';

  const requirement =
    experienceMin !== null && experienceMax !== null
      ? `${experienceMin}-${experienceMax} yrs`
      : experienceMin !== null
        ? `${experienceMin}+ yrs`
        : experienceMax !== null
          ? `up to ${experienceMax} yrs`
          : 'not set';

  return (
    <div className={styles['ui-experience']}>
      <div className={styles['ui-experience__header']}>
        <h3 className={styles['ui-experience__heading']}>Experience breakdown</h3>
        <span className={styles['ui-experience__score']}>{displayPct(scores.experience)}</span>
      </div>

      <div className={styles['ui-experience__tiles']}>
        <div className={styles['ui-experience__tile']}>
          <span className={styles['ui-experience__tile-label']}>Years of experience · weight 70%</span>
          <span className={styles['ui-experience__tile-value']}>
            {relevantYears === null ? '—' : `${relevantYears} yrs`}
          </span>
          <span className={styles['ui-experience__tile-detail']}>
            Job requires: {requirement} → {displayPct(scores.years)}
          </span>
        </div>
        <div className={styles['ui-experience__tile']}>
          <span className={styles['ui-experience__tile-label']}>Context relevance · weight 30%</span>
          <span className={styles['ui-experience__tile-value']}>{displayPct(scores.context)}</span>
          <span className={styles['ui-experience__tile-detail']}>
            Industry, company type, role and tech stack
          </span>
        </div>
      </div>

      <div className={styles['ui-experience__context']}>
        <span className={styles['ui-experience__context-title']}>Context match</span>
        <ul className={styles['ui-experience__factors']}>
          <li className={styles['ui-experience__factor']}>
            <span
              aria-hidden
              className={classNames(styles['ui-experience__dot'], styles['ui-experience__dot--base'])}
            />
            <span className={styles['ui-experience__factor-label']}>Base score</span>
            <span className={styles['ui-experience__bonus']}>{CONTEXT_BASE}%</span>
          </li>
          {factors.map((factor) => (
            <li className={styles['ui-experience__factor']} key={factor.id}>
              <span
                aria-hidden
                className={classNames(styles['ui-experience__dot'], {
                  [styles['ui-experience__dot--matched']]: factor.matched,
                })}
              />
              <span className={styles['ui-experience__factor-label']}>
                {FACTOR_LABELS[factor.id] || factor.id}
                {factor.matched ? '' : ' (not matched)'}
                {factor.note ? ` · ${factor.note}` : ''}
              </span>
              <span className={styles['ui-experience__bonus']}>
                {factor.matched ? `+${CONTEXT_BONUSES[factor.id] || 0}%` : '+0%'}
              </span>
            </li>
          ))}
        </ul>
        {reasoning !== '' && <p className={styles['ui-experience__reasoning']}>{reasoning}</p>}
      </div>

      <OverrideBox
        description={
          <>
            AI score: <strong>{displayPct(scores.experience)}</strong>. Adjust the components if
            you disagree.
          </>
        }
        fields={[
          { key: 'years_pct', label: 'Years score %' },
          { key: 'context_pct', label: 'Context relevance %' },
        ]}
        footnote="Experience % = (Years × 0.70) + (Context × 0.30) · Overall % = (Skills × 0.65) + (Experience × 0.35)"
        title="Override experience score"
      />
    </div>
  );
}
